import React, { useState, useRef } from 'react';
import apiClient from '../api/apiClient';
import CodeDisplay from '../components/CodeDisplay';
import ExcelUpload from '../components/ExcelUpload';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const BarcodeGenerator = () => {
  // State for form inputs
  const [barcodeIds, setBarcodeIds] = useState('1,2');
  const [bulkData, setBulkData] = useState(null);
  const [useBulk, setUseBulk] = useState(false);
  
  // State for response
  const [barcodeResponse, setBarcodeResponse] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // State for label toggle - default to true
  const [showLabel, setShowLabel] = useState(true);

  const env = import.meta.env.VITE_ENV || 'development';
  const apiUrl = import.meta.env.VITE_API_URL || 'https://localhost:7206';
  const responseContainerRef = useRef(null);

  const parseCommaList = (str) => {
    if (!str) return [];
    return str.split(',').map(s => s.trim()).filter(Boolean);
  };

  const generateBarcodes = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');
    setSuccess('');
    setBarcodeResponse([]);
    setProgress({ current: 0, total: 0 });

    try {
      let ids = [];
      let texts = [];
      
      if (useBulk && bulkData) {
        // Bulk upload - get IDs and optional texts from Excel
        ids = bulkData.map(row => row.id);
        texts = bulkData.map(row => row.text || row.id);
      } else {
        // Manual input
        ids = parseCommaList(barcodeIds);
        texts = ids;
      }

      if (ids.length === 0) {
        throw new Error('Please enter at least one ID.');
      }

      // Use the showLabel state from the checkbox
      const payload = {
        barcodeIds: ids,
        text: texts,
        showLabel: showLabel  // Use the state variable here
      };

      if (env === 'development') {
        console.log('Barcode Payload:', payload);
      }

      const response = await apiClient.post('/qr/bulk-barcodes', payload);

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error('Unexpected response format. Expected array.');
      }

      setBarcodeResponse(response.data);
      setSuccess(`✅ Successfully generated ${response.data.length} barcodes!`);
      
      setTimeout(() => {
        if (responseContainerRef.current) {
          responseContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
    } catch (err) {
      console.error('Barcode generation error:', err);
      if (err.response) {
        console.error('Server error details:', err.response.data);
        setError(`Server Error (${err.response.status}): ${err.response.data?.error || err.response.data || err.message}`);
      } else if (err.request) {
        setError('No response from server. Please check if the API is running.');
      } else {
        setError(err.message || 'Failed to generate barcodes.');
      }
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const clearResponse = () => {
    setBarcodeResponse([]);
    setSuccess('');
    setError('');
  };

  const loadSampleData = () => {
    setBarcodeIds('1,2,3,4,5');
    setUseBulk(false);
    setBulkData(null);
  };

  // Handle bulk data from Excel
  const handleBulkDataLoaded = (data) => {
    if (data) {
      setBulkData(data);
      setUseBulk(true);
      setBarcodeIds('');
    } else {
      setBulkData(null);
      setUseBulk(false);
    }
  };

  // Download all barcodes as ZIP
  const downloadAllBarcodes = async () => {
    if (barcodeResponse.length === 0) return;
    
    setDownloading(true);
    setError('');
    
    try {
      const zip = new JSZip();
      const folder = zip.folder('barcodes');
      
      barcodeResponse.forEach((item, index) => {
        const id = item.tankId || item.id || `barcode-${index + 1}`;
        const base64Data = item.qrCodeBase64 || item.barcodeBase64 || '';
        
        if (base64Data) {
          const binaryData = atob(base64Data);
          const array = new Uint8Array(binaryData.length);
          for (let i = 0; i < binaryData.length; i++) {
            array[i] = binaryData.charCodeAt(i);
          }
          
          folder.file(`barcode-${id}.png`, array);
        }
      });
      
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `barcodes-${new Date().getTime()}.zip`);
      
      setSuccess(`✅ Successfully downloaded ${barcodeResponse.length} barcodes as ZIP!`);
      
    } catch (err) {
      console.error('Download error:', err);
      setError(`Failed to download barcodes: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  // Download all barcodes as individual files
  const downloadAllBarcodesIndividual = () => {
    if (barcodeResponse.length === 0) return;
    
    barcodeResponse.forEach((item, index) => {
      const id = item.tankId || item.id || `barcode-${index + 1}`;
      const base64Data = item.qrCodeBase64 || item.barcodeBase64 || '';
      
      if (base64Data) {
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${base64Data}`;
        link.download = `barcode-${id}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
  };

  // Get records count
  const getRecordCount = () => {
    if (useBulk && bulkData) {
      return bulkData.length;
    }
    const ids = parseCommaList(barcodeIds);
    return ids.length;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>📊 Barcode Generator</h2>
        <div className="env-badge">
          <span className={`env-dot env-${env}`}></span>
          {env}
          <span className="env-url">{apiUrl}</span>
        </div>
      </div>

      <div className="content-wrapper">
        <div className="form-section">
          <form onSubmit={generateBarcodes}>
            {/* Input Mode Toggle */}
            <div className="input-mode-toggle">
              <button
                type="button"
                className={`btn ${!useBulk ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => {
                  setUseBulk(false);
                  setBulkData(null);
                }}
              >
                📝 Manual Input
              </button>
              <button
                type="button"
                className={`btn ${useBulk ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setUseBulk(true)}
              >
                📤 Bulk Upload
              </button>
            </div>

            {!useBulk ? (
              // Manual Input Mode
              <>
                {/* Label Toggle Checkbox */}
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={showLabel}
                      onChange={(e) => setShowLabel(e.target.checked)}
                    />
                    <span>Show text labels on barcodes</span>
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="barcodeIds">IDs (comma separated)</label>
                  <input
                    id="barcodeIds"
                    type="text"
                    value={barcodeIds}
                    onChange={(e) => setBarcodeIds(e.target.value)}
                    placeholder="e.g., 1,2,3"
                    className="input-field"
                    required
                  />
                  <small className="helper-text">
                    Enter IDs separated by commas to generate barcodes
                  </small>
                </div>

                <div className="record-count">
                  <span className="badge">📊 {getRecordCount()} record(s) to generate</span>
                  <span className="badge label-status">
                    {showLabel ? '🏷️ Labels ON' : '🏷️ Labels OFF'}
                  </span>
                </div>
              </>
            ) : (
              // Bulk Upload Mode
              <ExcelUpload 
                onDataLoaded={handleBulkDataLoaded}
                maxRows={1000}
                title="📤 Upload Excel for Barcodes"
                description="Upload Excel file with ID column (TEXT is optional)"
                sampleType="barcode"
              />
            )}

            <div className="button-group">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading || (useBulk && !bulkData)}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Generating {progress.current > 0 ? `${progress.current}/${progress.total}` : '...'}
                  </>
                ) : (
                  `▶ Generate Barcodes${useBulk && bulkData ? ` (${bulkData.length})` : ''}`
                )}
              </button>

              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={loadSampleData}
                disabled={useBulk}
              >
                📋 Load Sample
              </button>

              <button 
                type="button" 
                className="btn btn-danger"
                onClick={clearResponse}
                disabled={barcodeResponse.length === 0}
              >
                🗑️ Clear
              </button>
            </div>
          </form>

          {/* Progress Bar */}
          {loading && progress.total > 0 && (
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
              <span className="progress-text">
                {progress.current} / {progress.total} records processed
              </span>
            </div>
          )}

          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">❌</span>
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{error}</pre>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <span className="alert-icon">✅</span>
              {success}
            </div>
          )}
        </div>

        <div 
          className="response-section" 
          ref={responseContainerRef}
          style={{ maxHeight: '600px', display: 'flex', flexDirection: 'column' }}
        >
          <div className="response-header" style={{ flexShrink: 0 }}>
            <h3>Generated Barcodes</h3>
            <div className="response-header-actions">
              {barcodeResponse.length > 0 && (
                <span className="code-count">{barcodeResponse.length} codes</span>
              )}
            </div>
          </div>

          <div 
            className="response-scrollable"
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: '4px',
            }}
          >
            <CodeDisplay 
              items={barcodeResponse}
              emptyMessage="Enter data and click 'Generate Barcodes' to create barcodes"
            />
          </div>

          {barcodeResponse.length > 0 && (
            <div className="response-actions" style={{ flexShrink: 0, marginTop: 'auto' }}>
              <div className="download-actions">
                <button 
                  className="btn btn-primary"
                  onClick={downloadAllBarcodes}
                  disabled={downloading}
                >
                  {downloading ? (
                    <>
                      <span className="spinner"></span>
                      Creating ZIP...
                    </>
                  ) : (
                    '📦 Download All as ZIP'
                  )}
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={downloadAllBarcodesIndividual}
                  disabled={downloading}
                >
                  ⬇️ Download Individual
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    const summary = barcodeResponse.map(item => 
                      `ID: ${item.tankId || item.id}`
                    ).join('\n');
                    
                    const blob = new Blob([summary], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'barcodes-summary.txt';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  📝 Download Summary
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BarcodeGenerator;